import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "../../api/httpClient";
import { createInitialsAvatar } from "../../utils/initialsAvatar";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import StaffIdCard from "../../components/employee/StaffIdCard";
import useCurrentEmployee, { updateCurrentEmployeeCache } from "../../hooks/useCurrentEmployee";
import { buildAssetUrl, getApiBaseUrl } from "../../data/authStorage";
import OrganizationProfile from "./OrganizationProfile";
import ChangePasswordPanel from "../../components/profile/ChangePasswordPanel";
import EarningsDeductions from "../../components/payroll/EarningsDeductions";
import JobDescriptionPanel from "../../components/profile/JobDescriptionPanel";
import GuarantorFormsPanel from "../../components/profile/GuarantorFormsPanel";
import ProfileChangeRequestPanel from "../../components/profile/ProfileChangeRequestPanel";

const sections = [
  "Basic Info",
  "Organization",
  "Job Description",
  "Guarantor Forms",
  "Tools",
  "ID Card",
  "Education",
  "Experience",
  "Awards & Certification",
  "Employment & Benefits",
  "Earnings & Deductions",
];

const formatDate = (value) => {
  if (!value) return "-";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString();
};

const normalizeText = (value) => {
  if (value === null || value === undefined) {
    return "-";
  }

  const text = `${value}`.trim();
  return text || "-";
};

const labelValue = (label, value) => (
  <p className="text-sm text-[#071C50]">
    <span className="font-medium">{label}: </span>
    {normalizeText(value)}
  </p>
);

const renderCollection = (items, emptyText, renderer) => {
  if (!Array.isArray(items) || items.length === 0) {
    return <p className="text-sm text-gray-500">{emptyText}</p>;
  }

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div
          key={`${index}-${JSON.stringify(item)}`}
          className="rounded-lg border border-gray-100 p-3 text-sm text-[#071C50]"
        >
          {renderer(item, index)}
        </div>
      ))}
    </div>
  );
};

const Profile = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const apiBaseUrl = getApiBaseUrl();
  const requestedEmployeeId = searchParams.get("id") || "";
  const {
    companyAccount,
    currentEmployee,
    currentEmployeeName,
    currentEmployeeEmail,
    currentEmployeePhoto,
    loading: selfLoading,
    loginEmail,
  } = useCurrentEmployee();

  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(requestedEmployeeId);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [error, setError] = useState("");
  const [activeSection, setActiveSection] = useState("Basic Info");
  const [photoOverrideUrl, setPhotoOverrideUrl] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState("");

  useEffect(() => {
    setSelectedEmployeeId(requestedEmployeeId);
  }, [requestedEmployeeId]);

  useEffect(() => {
    if (!companyAccount) {
      return;
    }

    let isMounted = true;

    const loadEmployees = async () => {
      if (!apiBaseUrl) {
        setError("API base URL is not configured.");
        return;
      }

      try {
        setLoadingEmployees(true);
        setError("");

        const response = await axios.get(`${apiBaseUrl}/organizations/employees`);
        const nextEmployees = Array.isArray(response?.data?.employees) ? response.data.employees : [];

        if (isMounted) {
          setEmployees(nextEmployees);
        }
      } catch (requestError) {
        const message = requestError?.response?.data?.message || "Unable to load employee profiles.";
        if (isMounted) {
          setError(Array.isArray(message) ? message.join(", ") : message);
          setEmployees([]);
        }
      } finally {
        if (isMounted) {
          setLoadingEmployees(false);
        }
      }
    };

    loadEmployees();

    return () => {
      isMounted = false;
    };
  }, [apiBaseUrl, companyAccount]);

  const selectedEmployee = useMemo(() => {
    if (companyAccount) {
      return (
        employees.find((employee) => employee.id === selectedEmployeeId) ||
        employees[0] ||
        null
      );
    }

    return currentEmployee || null;
  }, [companyAccount, currentEmployee, employees, selectedEmployeeId]);

  useEffect(() => {
    if (companyAccount && !selectedEmployeeId && employees.length > 0) {
      setSelectedEmployeeId(employees[0].id);
    }
  }, [companyAccount, employees, selectedEmployeeId]);

  const profilePhoto = useMemo(() => {
    if (photoOverrideUrl) {
      return photoOverrideUrl;
    }

    const path = selectedEmployee?.profilePhotoUrl || currentEmployeePhoto;
    const fallbackName =
      selectedEmployee?.fullName ||
      selectedEmployee?.name ||
      `${selectedEmployee?.firstName || ""} ${selectedEmployee?.lastName || ""}`.trim() ||
      currentEmployeeName;
    return buildAssetUrl(path) || path || createInitialsAvatar(fallbackName);
  }, [currentEmployeeName, currentEmployeePhoto, photoOverrideUrl, selectedEmployee]);

  const employeeName =
    selectedEmployee?.fullName ||
    selectedEmployee?.name ||
    `${selectedEmployee?.firstName || ""} ${selectedEmployee?.lastName || ""}`.trim() ||
    currentEmployeeName ||
    "Employee profile";

  const employeeEmail =
    selectedEmployee?.officialEmail ||
    selectedEmployee?.personalEmail ||
    selectedEmployee?.emailAddress ||
    currentEmployeeEmail ||
    "-";

  const employeePhone = selectedEmployee?.phone || selectedEmployee?.phoneNumber || "-";
  const employeeId = selectedEmployee?.employeeNumber || selectedEmployee?.idNo || "-";
  const employeeLevel = selectedEmployee?.level?.name || selectedEmployee?.levelName || "-";
  const employeeDepartments = Array.from(new Set([
    ...(Array.isArray(selectedEmployee?.departments)
      ? selectedEmployee.departments.map((department) => department?.name)
      : []),
    selectedEmployee?.department?.name,
    selectedEmployee?.departmentName,
  ].filter(Boolean)));
  const employeeDepartment = employeeDepartments.join(", ") || "-";
  const employeePosition = selectedEmployee?.position?.name || selectedEmployee?.positionName || selectedEmployee?.jobTitle || "-";
  const employeeStatus = selectedEmployee?.isOffboarded
    ? "Offboarded"
    : selectedEmployee?.status || "Approved";
  const statusTone = employeeStatus === "Offboarded"
    ? {
      wrapper: "border-rose-100 bg-rose-50 text-rose-700",
      dot: "bg-rose-500",
      label: "Inactive profile",
    }
    : employeeStatus === "Pending"
      ? {
        wrapper: "border-amber-100 bg-amber-50 text-amber-700",
        dot: "bg-amber-500",
        label: "Awaiting approval",
      }
      : {
        wrapper: "border-emerald-100 bg-emerald-50 text-emerald-700",
        dot: "bg-emerald-500",
        label: "Active profile",
      };
  const canUpdateOwnPhoto = !companyAccount && !!selectedEmployee?.id;

  const handleProfilePhotoChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    if (!file.type?.startsWith("image/")) {
      setPhotoUploadError("Please choose an image file.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setPhotoUploadError("Profile picture is too large. Please choose an image that is 10 MB or smaller.");
      return;
    }

    if (!apiBaseUrl) {
      setPhotoUploadError("API base URL is not configured.");
      return;
    }

    const formData = new FormData();
    formData.append("profilePhoto", file);

    try {
      setUploadingPhoto(true);
      setPhotoUploadError("");

      const response = await axios.patch(
        `${apiBaseUrl}/organizations/employees/me/profile-photo`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const updatedEmployee = response?.data?.employee;
      const nextPhotoPath = updatedEmployee?.profilePhotoUrl;

      if (updatedEmployee) {
        updateCurrentEmployeeCache(loginEmail, {
          ...currentEmployee,
          ...updatedEmployee,
        });
      }

      setPhotoOverrideUrl(buildAssetUrl(nextPhotoPath) || nextPhotoPath || "");
    } catch (requestError) {
      const message = requestError?.response?.status === 413
        ? "Profile picture is too large. Please choose an image that is 10 MB or smaller."
        : requestError?.response?.data?.message || "Unable to update profile photo.";
      setPhotoUploadError(Array.isArray(message) ? message.join(", ") : message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const assignedTools = useMemo(() => {
    const toolAssignments = Array.isArray(selectedEmployee?.toolAssignments)
      ? selectedEmployee.toolAssignments
      : [];

    return toolAssignments
      .filter((assignment) => assignment?.status === "Assigned")
      .map((assignment) => assignment?.tool)
      .filter(Boolean);
  }, [selectedEmployee?.toolAssignments]);

  const basicInfoContent = (
    <div>
      <div className="space-y-3">
      {labelValue("Employee ID", employeeId)}
      {labelValue("Official Email", selectedEmployee?.officialEmail)}
      {labelValue("Personal Email", selectedEmployee?.personalEmail)}
      {labelValue("Phone", employeePhone)}
      {labelValue("Gender", selectedEmployee?.gender)}
      {labelValue("Marital Status", selectedEmployee?.maritalStatus)}
      {labelValue("Date of Birth", formatDate(selectedEmployee?.dateOfBirth))}
      {labelValue("State of Origin", selectedEmployee?.stateOrigin)}
      {labelValue("State of Residence", selectedEmployee?.stateResidence)}
      {labelValue("LGA", selectedEmployee?.lga)}
      {labelValue("Residential Address", selectedEmployee?.residentialAddress)}
      {labelValue("Created", formatDate(selectedEmployee?.createdAt))}
      {labelValue("Last Updated", formatDate(selectedEmployee?.updatedAt))}
      </div>
      <ProfileChangeRequestPanel employee={selectedEmployee} />
    </div>
  );

  const organizationContent = (
    <div className="space-y-3">
      {labelValue(employeeDepartments.length > 1 ? "Departments" : "Department", employeeDepartment)}
      {labelValue("Position", employeePosition)}
      {labelValue("Level", employeeLevel)}
      {labelValue("Branch", selectedEmployee?.branch?.name)}
      {labelValue("Date Joined", formatDate(selectedEmployee?.dateJoined))}
      {labelValue("Employment Type", selectedEmployee?.employmentType)}
      {labelValue("Status", employeeStatus)}
      {labelValue("Offboarded At", selectedEmployee?.offboardedAt ? formatDate(selectedEmployee.offboardedAt) : "-")}
      {labelValue("Notice Period", selectedEmployee?.offboardingNoticePeriod)}
      {labelValue(
        "Tools Assigned",
        (() => {
          const toolNames = Array.isArray(selectedEmployee?.toolAssignments)
            ? selectedEmployee.toolAssignments
                .filter((assignment) => assignment?.status === "Assigned")
                .map((assignment) => assignment?.tool?.name)
                .filter(Boolean)
            : [];

          return toolNames.length > 0
            ? toolNames.join(", ")
            : selectedEmployee?.offboardingToolsAssigned || selectedEmployee?.toolsAssigned || "-";
        })(),
      )}
    </div>
  );

  const toolsContent = assignedTools.length > 0 ? (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {assignedTools.map((tool) => (
          <div key={tool.id || tool.name} className="rounded-2xl border border-[#DCE3FF] bg-[#F4F7FF] px-4 py-3">
            <p className="font-medium text-[#071C50]">{tool.name}</p>
            <p className="text-xs text-slate-500">{tool.description || "Assigned tool"}</p>
          </div>
        ))}
      </div>
      <p className="text-sm text-slate-500">Tool withdrawals and reassignments are reflected here automatically.</p>
    </div>
  ) : (
    <p className="text-sm text-slate-500">No tools are currently assigned to this employee.</p>
  );

  const idCardContent = <StaffIdCard employee={selectedEmployee} photo={profilePhoto} />;

  const educationContent = renderCollection(selectedEmployee?.educationRecords, "No education records.", (item, index) => (
    <div className="space-y-2">
      {labelValue("Record", `${index + 1}`)}
      {labelValue("School", normalizeText(item.school))}
      {labelValue("Degree", normalizeText(item.degree))}
      {labelValue("Course", normalizeText(item.course))}
      {labelValue("Qualification Class", normalizeText(item.qualificationClass))}
      {labelValue("Start Year", normalizeText(item.startYear))}
      {labelValue("End Year", normalizeText(item.endYear))}
    </div>
  ));

  const experienceContent = renderCollection(selectedEmployee?.experienceRecords, "No experience records.", (item, index) => (
    <div className="space-y-2">
      {labelValue("Record", `${index + 1}`)}
      {labelValue("Company", normalizeText(item.company))}
      {labelValue("Position", normalizeText(item.position))}
      {labelValue("Employment Type", normalizeText(item.employmentType))}
      {labelValue("Start Date", formatDate(item.start))}
      {labelValue("End Date", formatDate(item.end))}
      {labelValue("Description", normalizeText(item.description))}
      {labelValue("Portfolio Link", normalizeText(item.portfolioLink))}
    </div>
  ));

  const awardsContent = renderCollection(selectedEmployee?.awardsRecords, "No award or certification records.", (item, index) => (
    <div className="space-y-2">
      {labelValue("Record", `${index + 1}`)}
      {labelValue("Award Name", normalizeText(item.nameOfAward))}
      {labelValue("Issued By", normalizeText(item.issuedBy))}
      {labelValue("Award Date", formatDate(item.awardDate))}
      {labelValue("Award Expiry", formatDate(item.expiryDate))}
      {labelValue("Certification", normalizeText(item.certification))}
      {labelValue("Certification Body", normalizeText(item.certificationBody))}
      {labelValue("Certification Expiry", formatDate(item.certificateExpiryDate))}
    </div>
  ));

  const benefitsContent = (
    <div className="space-y-3">
      {labelValue("Pension Provider", selectedEmployee?.pensionProvider)}
      {labelValue("Pension Number", selectedEmployee?.pensionNumber)}
      {labelValue("On HMO", selectedEmployee?.hmo)}
      {labelValue("HMO ID", selectedEmployee?.hmoId)}
      {labelValue("HMO Number", selectedEmployee?.hmoNumber)}
      {labelValue("HMO Status", selectedEmployee?.hmoStatus)}
      {labelValue("HMO Plan", selectedEmployee?.hmoPlan)}
      {labelValue("HMO Hospital", selectedEmployee?.hmoHospital)}
    </div>
  );

  if (companyAccount) {
    return <OrganizationProfile />;
  }

  if (selfLoading || loadingEmployees) {
    return <FetchLoadingState message="Loading profile details..." className="min-h-[260px]" />;
  }

  if (error) {
    return (
      <div className="p-6 font-Outfit">
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
        <button
          className="mt-4 rounded-lg border border-[#1234CD] px-4 py-2 text-[#1234CD]"
          onClick={() => navigate(-1)}
        >
          Go back
        </button>
      </div>
    );
  }

  if (!selectedEmployee) {
    return (
      <div className="p-6 font-Outfit text-sm text-gray-600">
        No employee profile is available yet.
      </div>
    );
  }

  const sectionContentMap = {
    "Basic Info": basicInfoContent,
    Organization: organizationContent,
    "Job Description": <JobDescriptionPanel />,
    "Guarantor Forms": <GuarantorFormsPanel />,
    Tools: toolsContent,
    "ID Card": idCardContent,
    Education: educationContent,
    Experience: experienceContent,
    "Awards & Certification": awardsContent,
    "Employment & Benefits": benefitsContent,
    "Earnings & Deductions": <EarningsDeductions embedded />,
  };

  return (
    <div className="p-4 font-Outfit sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-[20px] font-[700] leading-[30px] text-[#400167]">
            {companyAccount ? "Employee Profile" : "My Profile"}
          </h1>
          <p className="text-sm text-[#6B7280]">
            {companyAccount
              ? "Browse employee details or open the full staff record."
              : "View your staff record with read-only details."}
          </p>
        </div>

        {companyAccount ? (
          <div className="min-w-[240px]">
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-[#6B7280]">
              Select Employee
            </label>
            <select
              value={selectedEmployeeId || selectedEmployee?.id || ""}
              onChange={(event) => setSelectedEmployeeId(event.target.value)}
              className="w-full rounded-lg border border-[#D0D5DD] bg-white px-3 py-2 text-sm"
            >
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.fullName || employee.name || `${employee.firstName || ""} ${employee.lastName || ""}`.trim() || "Unnamed employee"}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      <div className="mb-5 rounded-xl bg-[#F4F4F8] p-4 sm:mb-6 sm:p-5">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-center">
          <div className="flex min-w-0 flex-col items-center gap-4 text-center sm:flex-row sm:items-center sm:gap-5 sm:text-left">
            <div className="flex flex-col items-center gap-2">
              <img
                src={profilePhoto}
                alt={employeeName}
                className="h-24 w-24 rounded-full object-cover sm:h-[100px] sm:w-[100px]"
              />
              {canUpdateOwnPhoto ? (
                <label className="cursor-pointer rounded-md border border-[#1234CD] px-3 py-1 text-xs font-medium text-[#1234CD] hover:bg-white">
                  {uploadingPhoto ? "Uploading..." : "Change photo"}
                  <input
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    disabled={uploadingPhoto}
                    onChange={handleProfilePhotoChange}
                  />
                </label>
              ) : null}
              {photoUploadError ? (
                <p className="max-w-[180px] text-center text-xs text-red-600" role="alert">
                  {photoUploadError}
                </p>
              ) : null}
            </div>
            <div className="min-w-0 max-w-full">
              <h2 className="break-words text-2xl font-semibold text-[#071C50] sm:text-[32px]">{employeeName}</h2>
              <p className="mt-1 break-all text-sm text-[#071C50] sm:break-normal">{employeeEmail}</p>
              <p className="text-sm text-[#071C50]">{employeePhone}</p>
              <p className="mt-1 text-[#071C50] text-sm">Employee ID: {employeeId}</p>
              <p className="mt-1 text-[#071C50] text-sm">
                Level: <span className="rounded-md bg-[#DDEAFB] px-2 py-1 text-sm">{employeeLevel}</span>
              </p>
              {companyAccount ? (
                <div className="mt-4 flex gap-2 text-sm font-medium">
                  <button
                    type="button"
                    onClick={() => navigate(`/employee/employeespage?id=${encodeURIComponent(selectedEmployee.id)}`)}
                    className="text-[#12CD18] hover:underline"
                  >
                    View full record
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/employee/new-employee`)}
                    className="text-[#1234CD] hover:underline"
                  >
                    Onboard staff
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          <div className={`w-full rounded-2xl border px-5 py-4 shadow-sm md:w-auto md:min-w-[220px] ${statusTone.wrapper}`}>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] opacity-70">Status</p>
                <p className="mt-1 text-xl font-semibold">{employeeStatus}</p>
                <p className="mt-1 text-xs opacity-75">{statusTone.label}</p>
              </div>
              <span className={`h-3 w-3 rounded-full shadow-[0_0_0_6px_rgba(255,255,255,0.85)] ${statusTone.dot}`} />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 md:flex-row">
        <div className="sticky top-0 z-20 -mx-4 border-y border-slate-100 bg-white/95 px-4 py-3 shadow-sm backdrop-blur md:hidden">
          <div className="grid grid-cols-[auto,1fr,auto] gap-2">
            <button type="button" disabled={activeSection === sections[0]} onClick={() => setActiveSection(sections[Math.max(0, sections.indexOf(activeSection) - 1)])} className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-[#1234CD] disabled:opacity-35" aria-label="Previous profile section">&larr;</button>
            <select value={activeSection} onChange={(event) => setActiveSection(event.target.value)} className="min-w-0 rounded-lg border border-[#D0D5DD] bg-white px-3 py-2 text-sm font-semibold text-[#071C50]">
              {sections.map((section) => <option key={section} value={section}>{section}</option>)}
            </select>
            <button type="button" disabled={activeSection === sections[sections.length - 1]} onClick={() => setActiveSection(sections[Math.min(sections.length - 1, sections.indexOf(activeSection) + 1)])} className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-[#1234CD] disabled:opacity-35" aria-label="Next profile section">&rarr;</button>
          </div>
        </div>

        <div className="hidden md:block md:w-[220px] md:border-r md:pr-4">
          {sections.map((section) => (
            <button
              key={section}
              type="button"
              onClick={() => setActiveSection(section)}
              className={`block w-full px-4 py-2 text-left text-base ${
                activeSection === section
                  ? "border-b-2 border-[#1234CD] font-medium text-black"
                  : "text-[#071C50] hover:bg-gray-50"
              }`}
            >
              {section}
            </button>
          ))}
        </div>

        <div className="min-w-0 flex-1 md:px-4">
          <h2 className="mb-4 text-2xl font-semibold text-[#071C50] sm:text-[30px]">{activeSection}</h2>
          <div className="text-[#071C50]">{sectionContentMap[activeSection]}</div>
        </div>
      </div>
      <ChangePasswordPanel className="mt-6" />
    </div>
  );
};

export default Profile;
